import {
  IconButton,
  Space,
  StatusBadge,
  type StatusBadgeStatus,
  SubText,
  Text,
  WithPopover,
} from '@procurify/ui'
import { type MessageDescriptor } from 'react-intl'

import { useProcurifyIntl } from '@webapp/hooks'
import { TravelApprovalLogActions } from '@webapp/modules/expense/entities/enums/TravelApprovalLogActionEnum'
import { type TravelApprovalLog } from '@webapp/modules/expense/entities/TravelApprovalLog'
import messages from './messages'
import { HistoryPopoverContentStyled } from './styles/HistoryPopoverContentStyled'

export interface ITravelApprovalHistoryPopoverProps {
  travelId: number
  logs: TravelApprovalLog[]
}

const BADGE_TYPE_BY_ACTION: Record<number, StatusBadgeStatus> = {
  [TravelApprovalLogActions.REQUEST]: 'inProgress',
  [TravelApprovalLogActions.FINAL_APPROVE]: 'success',
  [TravelApprovalLogActions.DENY]: 'error',
  [TravelApprovalLogActions.PURCHASE]: 'success',
}

const BADGE_MESSAGE_BY_ACTION: Record<number, MessageDescriptor> = {
  [TravelApprovalLogActions.REQUEST]: messages.logRequestedBadge,
  [TravelApprovalLogActions.FINAL_APPROVE]: messages.logApprovedBadge,
  [TravelApprovalLogActions.DENY]: messages.logDeniedBadge,
  [TravelApprovalLogActions.PURCHASE]: messages.logPurchasedBadge,
}

const SENTENCE_MESSAGE_BY_ACTION: Record<number, MessageDescriptor> = {
  [TravelApprovalLogActions.REQUEST]: messages.logRequestedSentence,
  [TravelApprovalLogActions.FINAL_APPROVE]: messages.logApprovedSentence,
  [TravelApprovalLogActions.DENY]: messages.logDeniedSentence,
  [TravelApprovalLogActions.PURCHASE]: messages.logPurchasedSentence,
}

/**
 * History icon trigger shown when the detail carries embedded `logs`
 * (BHV-061 — embedded-detail rule, no second query); opens a popover
 * listing the approval history entries.
 */
export const TravelApprovalHistoryPopover = ({
  travelId,
  logs,
}: ITravelApprovalHistoryPopoverProps) => {
  const intl = useProcurifyIntl()

  if (logs.length === 0) return null

  return (
    <WithPopover
      trigger='click'
      content={
        <HistoryPopoverContentStyled>
          <Space size='small'>
            {logs.map((log) => (
              <Space key={log.id} size='small'>
                <Space direction='horizontal' align='center' size='small'>
                  <StatusBadge
                    type={BADGE_TYPE_BY_ACTION[log.action] ?? 'inactive'}
                    text={intl.formatMessage(
                      BADGE_MESSAGE_BY_ACTION[log.action] ??
                        messages.logRequestedBadge
                    )}
                  />
                  <Text bold autoMargin={false}>
                    {intl.formatMessage(messages.actionByLabel, {
                      user: log.userFullName,
                    })}
                  </Text>
                </Space>
                <SubText autoMargin={false}>
                  {intl.formatDate(log.dateTime, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </SubText>
                <Text autoMargin={false}>
                  {intl.formatMessage(
                    SENTENCE_MESSAGE_BY_ACTION[log.action] ??
                      messages.logRequestedSentence,
                    { travelId }
                  )}
                </Text>
              </Space>
            ))}
          </Space>
        </HistoryPopoverContentStyled>
      }
    >
      <IconButton
        icon='action-history'
        aria-label={intl.formatMessage(messages.historyTriggerAriaLabel)}
      />
    </WithPopover>
  )
}

TravelApprovalHistoryPopover.displayName = 'TravelApprovalHistoryPopover'
